import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { SaveSpecialDebtsRequest } from "../../../../api/ClientFileApi";
import { useBankruptcyChecklistApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { ChecklistAnswer } from "../../../BankruptcyChecklist.types";
import { useBankruptcyChecklist } from "../../../hooks/useBankruptcyChecklist";
import { SpecialDebtsAnswerKey, SpecialDebtsFormValues } from "../SpecialDebts.types";
import { EMPTY_SPECIAL_DEBTS, applyAnswer, mapResponseToSpecialDebts } from "../SpecialDebts.utils";

function toRemark(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function toRequest(values: SpecialDebtsFormValues): SaveSpecialDebtsRequest {
  return {
    wasPreviouslySelfEmployed: values.wasPreviouslySelfEmployed,
    isCurrentlySelfEmployed: values.isCurrentlySelfEmployed,
    hasBusinessLicense: values.hasBusinessLicense,
    exercisesTradeAsBusiness: values.exercisesTradeAsBusiness,
    isBusinessLicenseDormant: values.isBusinessLicenseDormant,
    hasMaintenanceArrears: values.hasMaintenanceArrears,
    hasCriminalDebts: values.hasCriminalDebts,
    hasCreditDebts: values.hasCreditDebts,
    creditWithinLastFiveYears: values.creditWithinLastFiveYears,
    creditRemark: toRemark(values.creditRemark),
    hasMailOrderDebts: values.hasMailOrderDebts,
    mailOrderWithinLastThreeYears: values.mailOrderWithinLastThreeYears,
    mailOrderRemark: toRemark(values.mailOrderRemark),
    hasNeverPaidDebts: values.hasNeverPaidDebts,
  };
}

export function useSpecialDebts(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();
  const bankruptcyChecklistApi = useBankruptcyChecklistApi();

  const { data } = useBankruptcyChecklist(clientId);

  const saveMutation = useMutation({
    mutationFn: (request: SaveSpecialDebtsRequest) =>
      bankruptcyChecklistApi.saveBankruptcyChecklistSpecialDebts({
        clientId,
        saveSpecialDebtsRequest: request,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData([ClientFileQueryKey.BankruptcyChecklist, clientId], saved);
      showSuccess(t("clients.client.debtSettlement.bankruptcyChecklist.saveSuccess"));
    },
  });

  const initialValues: SpecialDebtsFormValues = useMemo(
    () => (data ? mapResponseToSpecialDebts(data) : EMPTY_SPECIAL_DEBTS),
    [data]
  );

  const formik = useFormikWithDirtyForm<SpecialDebtsFormValues>({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await saveMutation.mutateAsync(toRequest(values));
    },
  });

  const setAnswer = (key: SpecialDebtsAnswerKey, value: ChecklistAnswer) => {
    void formik.setValues(applyAnswer(formik.values, key, value));
  };

  const setRemark = (key: "creditRemark" | "mailOrderRemark", value: string) => {
    void formik.setFieldValue(key, value);
  };

  return { formik, setAnswer, setRemark };
}
